import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import SuperadminLayout, { DarkCard } from "./SuperadminLayout";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass } from "@phosphor-icons/react";

export default function SuperadminAudit() {
  const [items, setItems] = useState([]);
  const [hist, setHist] = useState([]);
  const [q, setQ] = useState("");
  const [event, setEvent] = useState("");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (q) params.q = q;
      if (event) params.event = event;
      const { data } = await api.get("/superadmin/audit", { params });
      setItems(data.items);
      setHist(data.histogram);
    } finally { setLoading(false); }
  }, [q, event, days]);

  useEffect(() => { load(); }, [load]);

  return (
    <SuperadminLayout title="Audit RGPD">
      <DarkCard className="p-4 mb-6" data-testid="audit-filters">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8494A8]" />
            <Input placeholder="Recherche libre (event, email, resource…)" value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]" data-testid="audit-search" />
          </div>
          <Select value={event || "all"} onValueChange={(v) => setEvent(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-[#081221] border-white/10 text-white" data-testid="audit-event"><SelectValue placeholder="Événement" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous événements</SelectItem>
              {hist.map((h) => <SelectItem key={h.event} value={h.event}>{h.event} ({h.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="bg-[#081221] border-white/10 text-white" data-testid="audit-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 derniers jours</SelectItem>
              <SelectItem value="30">30 derniers jours</SelectItem>
              <SelectItem value="90">90 derniers jours</SelectItem>
              <SelectItem value="365">1 an</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </DarkCard>

      <DarkCard className="p-4 mb-6" data-testid="audit-hist">
        <p className="text-xs uppercase tracking-widest text-[#8494A8] mb-3">Top 20 événements sur la période</p>
        <div className="flex flex-wrap gap-2">
          {hist.map((h) => (
            <button key={h.event} onClick={() => setEvent(h.event)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono transition ${event === h.event ? "bg-[#E4572E] text-white" : "bg-white/5 text-[#B7C4D6] hover:bg-white/10"}`}
              data-testid={`hist-${h.event}`}>
              {h.event} · <span className="text-[#8494A8]">{h.count}</span>
            </button>
          ))}
        </div>
      </DarkCard>

      <DarkCard className="overflow-hidden" data-testid="audit-table">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[#8494A8]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Événement</th>
              <th className="text-left px-4 py-3 font-medium">Utilisateur</th>
              <th className="text-left px-4 py-3 font-medium">Ressource</th>
              <th className="text-left px-4 py-3 font-medium">IP</th>
              <th className="text-left px-4 py-3 font-medium">Détails</th>
              <th className="text-left px-4 py-3 font-medium">Horodatage</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-[#8494A8]">Chargement…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#8494A8]">Aucun événement</td></tr>}
            {items.map((it, i) => (
              <tr key={i} className="border-t border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3">
                  <Badge className={`${it.event.startsWith("superadmin.") ? "bg-[#E4572E]/25 text-[#FFB59E]" : "bg-white/10 text-white/80"} hover:bg-white/10 font-mono text-[10px]`}>
                    {it.event}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-[#B7C4D6] text-xs break-all">{it.user_email || "—"}</td>
                <td className="px-4 py-3 text-[#B7C4D6] text-xs">
                  {it.resource_type && <span className="text-[#8494A8]">{it.resource_type}</span>}
                  {it.resource_id && <code className="ml-1 text-white/80">{it.resource_id.slice(0, 24)}</code>}
                </td>
                <td className="px-4 py-3 text-[#8494A8] text-xs font-mono">{it.ip || "—"}</td>
                <td className="px-4 py-3 text-[#B7C4D6] text-xs">
                  {it.extra && Object.keys(it.extra).length > 0 && (
                    <details>
                      <summary className="cursor-pointer text-[#8494A8]">voir</summary>
                      <pre className="mt-1 text-[10px] max-w-xs overflow-auto text-[#8494A8] bg-black/30 p-2 rounded">{JSON.stringify(it.extra, null, 2)}</pre>
                    </details>
                  )}
                </td>
                <td className="px-4 py-3 text-[#8494A8] text-xs whitespace-nowrap">{new Date(it.at).toLocaleString("fr-FR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DarkCard>
    </SuperadminLayout>
  );
}
